import Component from '@glimmer/component'
import { set, action } from '@ember/object'
import { isEmpty } from '@ember/utils'
import { A } from '@ember/array'
import DataTableOperations, { dtoComputed } from '@blakeelearning/data-tables/components/data-table-operations'
import { SortIcon, statefulSort } from 'district-ui-client/utils/giraffe/stateful-sort'
import { tracked } from '@glimmer/tracking'
import type School from 'district-ui-client/models/school'
import { Panel } from 'district-ui-client/components/panel'
import { eq } from 'ember-truth-helpers'
import { on } from '@ember/modifier'
import { fn } from '@ember/helper'
import { t } from 'ember-intl'
import { PhosphorIcon } from 'district-ui-client/components/phosphor-icon'
import { formatValue } from 'district-ui-client/utils/format-value'
import formatDuration from 'district-ui-client/helpers/format-duration'
import { BasePageSelector } from 'district-ui-client/components/base/page-selector'
import { hasId } from 'district-ui-client/utils/type-guards'
import type { TimeOnTaskHistoryDistrictResponse } from 'district-ui-client/utils/student-events/fetchers/time-on-task-history'

interface SchoolViewModel {
  id: string
  name: string
  students: number
  session_count: number
  total_seconds: number
  average_seconds_per_week: number
  average_seconds_per_session: number
}

interface Signature {
  Args: {
    data: TimeOnTaskHistoryDistrictResponse
    schools: School[]
  }
}

type DTOComponentContext = any

export class TimeOnTaskTable extends Component<Signature> {
  @tracked currentPage: number = 1

  @tracked perPage: number = 10

  @tracked currentSortColumn: Nullable<string> = null

  @tracked currentSortIcon: SortIcon = SortIcon.Default

  get districtTotal() {
    const districtTotal = this.args.data.district
    return districtTotal
  }

  get dataOperations() {
    return {
      doPagination: dtoComputed(
        'rows.[]',
        'currentPage',
        'perPage',
        function (this: DTOComponentContext, data: SchoolViewModel[]) {
          if (isEmpty(this.currentPage) || isEmpty(this.perPage)) return data
          const upperIndex = this.currentPage * this.perPage
          const lowerIndex = (this.currentPage - 1) * this.perPage
          return A(data).slice(lowerIndex, upperIndex)
        },
      ),
    }
  }

  get pageCount() {
    const dataLength = this.rows?.length
    const { perPage } = this
    return Math.ceil(dataLength / perPage)
  }

  operationsStack(this: DTOComponentContext) {
    return [...this.defaultOperationsStack, 'doPagination']
  }

  get rows() {
    const { schools } = this.args
    const schoolsData = this.args.data.schools
    const rows = schoolsData.reduce<SchoolViewModel[]>((acc, schoolData) => {
      const schoolId = schoolData.school_id
      const school = schools.find((school) => school.id === String(schoolId))
      if (!school || !hasId(school)) return acc

      const row = {
        id: school.id,
        name: school.name,
        students: schoolData.student_count,
        session_count: schoolData.session_count,
        total_seconds: schoolData.total_seconds,
        average_seconds_per_week: schoolData.average_seconds_per_week,
        average_seconds_per_session: schoolData.average_seconds_per_session,
      }
      return [...acc, row]
    }, [])

    return rows
  }

  statefulSort = (...args: Parameters<typeof statefulSort>) => {
    return statefulSort.apply(this, args)
  }

  @action
  changePage(newPageNumber: number) {
    set(this, 'currentPage', newPageNumber)
  }

  <template>
    <div>
      <Panel>
        <DataTableOperations
          @data={{this.rows}}
          @dataOperations={{this.dataOperations}}
          @operationsStack={{this.operationsStack}}
          @currentPage={{this.currentPage}}
          @perPage={{this.perPage}}
          as |transformedData tableAction|
        >
          <table data-test-tot-table class="disco-table w-full">
            <thead>
              <tr>
                <th
                  role="button"
                  class={{if (eq this.currentSortColumn "name") "selected"}}
                  {{on "click" (fn this.statefulSort tableAction "sortByValue" "name")}}
                >
                  <div class="flex items-center justify-between">
                    {{t "components.reporting.timeOnTask.table.allSchools"}}
                    <PhosphorIcon
                      @icon={{if (eq this.currentSortColumn "name") this.currentSortIcon "phosphor-caret-up-down-fill"}}
                      role="presentation"
                    />
                  </div>
                </th>
                <th
                  role="button"
                  class={{if (eq this.currentSortColumn "students") "selected"}}
                  {{on "click" (fn this.statefulSort tableAction "sortByNumber" "students")}}
                >
                  <div class="flex items-center justify-between">
                    {{t "components.reporting.timeOnTask.table.students"}}
                    <PhosphorIcon
                      @icon={{if
                        (eq this.currentSortColumn "students")
                        this.currentSortIcon
                        "phosphor-caret-up-down-fill"
                      }}
                      role="presentation"
                    />
                  </div>
                </th>
                <th
                  role="button"
                  class={{if (eq this.currentSortColumn "total_seconds") "selected"}}
                  {{on "click" (fn this.statefulSort tableAction "sortByNumber" "total_seconds")}}
                >
                  <div class="flex items-center justify-between">
                    {{t "components.reporting.timeOnTask.table.totalHours"}}
                    <PhosphorIcon
                      @icon={{if
                        (eq this.currentSortColumn "total_seconds")
                        this.currentSortIcon
                        "phosphor-caret-up-down-fill"
                      }}
                      role="presentation"
                    />
                  </div>
                </th>
                <th
                  role="button"
                  class={{if (eq this.currentSortColumn "average_seconds_per_week") "selected"}}
                  {{on "click" (fn this.statefulSort tableAction "sortByNumber" "average_seconds_per_week")}}
                >
                  <div class="flex items-center justify-between">
                    {{t "components.reporting.timeOnTask.table.avgHoursByWeek"}}
                    <PhosphorIcon
                      @icon={{if
                        (eq this.currentSortColumn "average_seconds_per_week")
                        this.currentSortIcon
                        "phosphor-caret-up-down-fill"
                      }}
                      role="presentation"
                    />
                  </div>
                </th>
                <th
                  role="button"
                  class={{if (eq this.currentSortColumn "session_count") "selected"}}
                  {{on "click" (fn this.statefulSort tableAction "sortByNumber" "session_count")}}
                >
                  <div class="flex items-center justify-between">
                    {{t "components.reporting.timeOnTask.table.totalNumberOfSessions"}}
                    <PhosphorIcon
                      @icon={{if
                        (eq this.currentSortColumn "session_count")
                        this.currentSortIcon
                        "phosphor-caret-up-down-fill"
                      }}
                      role="presentation"
                    />
                  </div>
                </th>
                <th
                  role="button"
                  class={{if (eq this.currentSortColumn "average_seconds_per_session") "selected"}}
                  {{on "click" (fn this.statefulSort tableAction "sortByNumber" "average_seconds_per_session")}}
                >
                  <div class="flex items-center justify-between">
                    {{t "components.reporting.timeOnTask.table.avgTimeInSession"}}
                    <PhosphorIcon
                      @icon={{if
                        (eq this.currentSortColumn "average_seconds_per_session")
                        this.currentSortIcon
                        "phosphor-caret-up-down-fill"
                      }}
                      role="presentation"
                    />
                  </div>
                </th>
              </tr>
            </thead>
            <tbody>
              <tr class="hover:bg-blue-50">
                <td>{{t "components.reporting.timeOnTask.table.total"}}</td>
                <td>
                  {{formatValue this.districtTotal.student_count}}
                </td>
                <td>
                  {{formatDuration this.districtTotal.total_seconds}}
                </td>
                <td>
                  {{formatDuration this.districtTotal.average_seconds_per_week}}
                </td>
                <td>
                  {{formatValue this.districtTotal.session_count}}
                </td>
                <td>
                  {{formatDuration this.districtTotal.average_seconds_per_session}}
                </td>
              </tr>
              {{#each transformedData as |row|}}
                <tr class="hover:bg-blue-50">
                  <td>
                    {{formatValue row.name}}
                  </td>
                  <td>
                    {{formatValue row.students}}
                  </td>
                  <td>
                    {{formatDuration row.total_seconds}}
                  </td>
                  <td>
                    {{formatDuration row.average_seconds_per_week}}
                  </td>
                  <td>
                    {{formatValue row.session_count}}
                  </td>
                  <td>
                    {{formatDuration row.average_seconds_per_session}}
                  </td>
                </tr>
              {{/each}}
            </tbody>
          </table>
        </DataTableOperations>

        {{! Table for export }}
        <div class="hidden">
          <DataTableOperations
            @data={{this.rows}}
            @dataOperations={{this.dataOperations}}
            @operationsStack={{this.operationsStack}}
            as |transformedData|
          >
            <table exportable>
              <thead>
                <tr>
                  <th>{{t "components.reporting.timeOnTask.table.allSchools"}}</th>
                  <th>{{t "components.reporting.timeOnTask.table.students"}}</th>
                  <th>{{t "components.reporting.timeOnTask.table.totalHours"}}</th>
                  <th>{{t "components.reporting.timeOnTask.table.avgHoursByWeek"}}</th>
                  <th>{{t "components.reporting.timeOnTask.table.totalNumberOfSessions"}}</th>
                  <th>{{t "components.reporting.timeOnTask.table.avgTimeInSession"}}</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>{{t "components.reporting.timeOnTask.table.total"}}</td>
                  <td>
                    {{formatValue this.districtTotal.student_count}}
                  </td>
                  <td>
                    {{formatDuration this.districtTotal.total_seconds}}
                  </td>
                  <td>
                    {{formatDuration this.districtTotal.average_seconds_per_week}}
                  </td>
                  <td>
                    {{formatValue this.districtTotal.session_count}}
                  </td>
                  <td>
                    {{formatDuration this.districtTotal.average_seconds_per_session}}
                  </td>
                </tr>
                {{#each transformedData as |row|}}
                  <tr>
                    <td>
                      {{formatValue row.name}}
                    </td>
                    <td>
                      {{formatValue row.students}}
                    </td>
                    <td>
                      {{formatDuration row.total_seconds}}
                    </td>
                    <td>
                      {{formatDuration row.average_seconds_per_week}}
                    </td>
                    <td>
                      {{formatValue row.session_count}}
                    </td>
                    <td>
                      {{formatDuration row.average_seconds_per_session}}
                    </td>
                  </tr>
                {{/each}}
              </tbody>
            </table>
          </DataTableOperations>
        </div>
        {{#unless (eq this.rows.length 0)}}
          <BasePageSelector
            class="mt-2"
            @pageCount={{this.pageCount}}
            @currentPage={{this.currentPage}}
            @changePage={{this.changePage}}
          />
        {{/unless}}
      </Panel>
    </div>
  </template>
}

export default TimeOnTaskTable
